import * as React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { useQuery } from '@apollo/client';

import usePersonalizationSettingsAttributes from 'bundles/personalization-settings/hooks/usePersonalizationSettingsAttributes';
import type { PersonalizationFieldValues } from 'bundles/personalization-settings/utils/formatLearnerObjectAttributes';

const EMPTY_VALUES: PersonalizationFieldValues = {
  learningGoal: null,
  desiredRole: null,
  skills: null,
  currentRole: null,
  educationLevel: null,
};

export type PersonalizationSettingsContextValue = {
  /** The current value per field -- the page-level query's seed, patched in place by each field's own saves. */
  values: PersonalizationFieldValues;
  loading: boolean;
  error: ReturnType<typeof useQuery>['error'];
  /** Patches a single field's live value, e.g. after that field's own mutation succeeds. */
  setFieldValue: <K extends keyof PersonalizationFieldValues>(field: K, value: PersonalizationFieldValues[K]) => void;
};

const PersonalizationSettingsContext = createContext<PersonalizationSettingsContextValue | undefined>(undefined);

export const usePersonalizationSettingsContext = (): PersonalizationSettingsContextValue => {
  const context = useContext(PersonalizationSettingsContext);
  if (!context) {
    throw new Error('usePersonalizationSettingsContext must be used within a PersonalizationSettingsProvider');
  }
  return context;
};

/**
 * Holds the Personalization settings' live field values: seeded from the
 * page-level query, then patched in place as each field's own mutation
 * succeeds. This lets every item component (and any future consumer, e.g. a
 * completeness summary) read the same up-to-date value without prop-drilling
 * or waiting on a refetch.
 */
export const PersonalizationSettingsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { values: queryValues, loading, error } = usePersonalizationSettingsAttributes();
  // Patches only -- the query's own value is read fresh each render below,
  // so `values` never lags a render behind a `loading` flip (an effect-based
  // copy would settle one render late, which is invisible to end users but
  // breaks any consumer that freezes a seed the moment `loading` goes false).
  const [patches, setPatches] = useState<Partial<PersonalizationFieldValues>>({});

  const setFieldValue = useCallback<PersonalizationSettingsContextValue['setFieldValue']>((field, value) => {
    setPatches((prev) => ({ ...prev, [field]: value }));
  }, []);

  const values = useMemo<PersonalizationFieldValues>(
    () => ({ ...(loading ? EMPTY_VALUES : queryValues), ...patches }),
    [loading, queryValues, patches]
  );

  const value = useMemo<PersonalizationSettingsContextValue>(
    () => ({ values, loading, error, setFieldValue }),
    [values, loading, error, setFieldValue]
  );

  return <PersonalizationSettingsContext.Provider value={value}>{children}</PersonalizationSettingsContext.Provider>;
};

export default PersonalizationSettingsContext;
